import { PencilIcon, PlusIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { CoproTableData } from '../../../@types/CoproTableData';
import { setCoprosFormData } from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { coproTypes } from '../../../utils/copros';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import CoproForm from '../../common/CoproForm';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';

const matchedKeys = ['id', 'remoteId', 'name', 'batchSize', 'type'];
const headerKeys = ['id', 'N° IMMAT', 'NOM', 'NB LOTS HAB / LOCAL PRO', 'TYPE'];
const sortableColumns = {
  'N° IMMAT': 'string', NOM: 'string', ADRESSE: 'string', CP: 'string', COMMUNE: 'string', 'NB LOTS HAB / LOCAL PRO': 'number', TYPE: 'string', 'REPRÉSENTANT LÉGAL': 'string', 'VALIDATION ADMIN': 'string',
};

function CoproValidation() {
  const dispatch = useAppDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [data, setData] = useState<Partial<CoproTableData>[]>(null);
  const [status, setStatus] = useState('IDLE');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingCopro, setEditingCopro] = useState(null);
  const [loading, setLoading] = useState<string | number>('');

  const openEditPopup = (copro) => {
    setEditingCopro(copro);
    setIsPopupOpen(true);
  };

  const setAdminValidation = async(id : number, validatedByAdmin : boolean) => {
    let url = `/api/copros/${id}/`;
    url += validatedByAdmin ? 'accept' : 'refuse';
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
    });

    if (!response.ok) {
      console.error('Error setting admin validation :', response);
      showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
    }
  };

  const fetchData = async() => {
    setIsPopupOpen(false);
    setStatus('PENDING');
    try {
      const response = await fetch(`/api/companies/${company.id}/copros?limit=10000&filterValidatedByUser=true&filterIncludeSubcontracted=false&sortField=id&sortDirection=desc`, JWTHeader(jwt));
      const result = await response.json();
      dispatch(setCoprosFormData(result.results));

      const formattedData = result.results.map((item) => {
        const newItem: Partial<CoproTableData> = {};

        // Add the actions column
        newItem.MODIFIER = (
          <div className="flex w-full items-center justify-center">
            <PencilIcon
              className="h-5 w-5 cursor-pointer text-gray-600 hover:text-gray-800"
              onClick={() => openEditPopup(item.id)}
            />
          </div>
        );

        matchedKeys.forEach((key, index) => {
          newItem[headerKeys[index]] = item[key];
        });

        newItem.TYPE = coproTypes[item.type];
        newItem.VALIDE = item.validatedByAdmin;
        newItem.validatedByAdmin = item.validatedByAdmin;

        if (newItem.validatedByAdmin === null) {
          newItem['VALIDATION ADMIN'] = <div className="flex h-full w-full items-center justify-center bg-amber-400 text-center text-black">A valider</div>;
          newItem.VALIDER = (
            <button
              onClick={async() => {
                await setAdminValidation(item.id, true);
                showToast('success', 'La copropriété a bien été validée.');
                fetchData();
              }}
              className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-2 text-base whitespace-nowrap text-white shadow-md"
            >
              Valider
            </button>
          );
        }
        if (newItem.validatedByAdmin === false) {
          newItem['VALIDATION ADMIN'] = <div className="flex h-full w-full items-center justify-center bg-red-600 text-center text-white">Refusée</div>;
          newItem.VALIDER = <div className="flex h-full w-full items-center justify-center bg-red-600 text-center text-white">Refusée</div>;
        }
        if (newItem.validatedByAdmin === true) {
          newItem['VALIDATION ADMIN'] = <div className="flex h-full w-full items-center justify-center bg-rgpd-green text-center text-white">Validée</div>;
          newItem.VALIDER = <div className="flex h-full w-full items-center justify-center bg-rgpd-green text-center text-white">Validée</div>;
        }

        if (item.addresses.length === 0) {
          newItem.ADRESSE = '';
          newItem.CP = '';
          newItem.COMMUNE = '';
        } else {
          newItem.ADRESSE = item.addresses[0].address;
          newItem.CP = item.addresses[0].postalCode;
          newItem.COMMUNE = item.addresses[0].city;
        }
        newItem.validatedByUser = item.validatedByUser;
        newItem.termEndAt = item.termEndAt;

        // show REPRESENTANT LÉGAL FROM IMPORT FILE
        const raisonSocialFile = (item.details && item.details.identificationDuRepresentantLegalRaisonSocialeEtLeNume) ?? '';
        const siretFile = (item.details && item.details.siretRepresentantLegalSiExiste) || '';
        newItem.isRepresentantLegalEqualToCompanyFile = siretFile ? siretFile.startsWith(company.siren) : false;
        newItem['REPRÉSENTANT LÉGAL'] = (
          raisonSocialFile && (
            <div className={newItem.isRepresentantLegalEqualToCompanyFile
              ? 'flex h-full w-full items-center justify-center bg-rgpd-green text-center text-xs text-rgpd-white' : 'flex h-full w-full items-center justify-center bg-red-600 text-center text-xs text-rgpd-white'}
            >
              {raisonSocialFile}
            </div>
          )
        );

        // same from the API
        const siretAPI = (item.details && item.details.siret) || '';
        newItem.isRepresentantLegalEqualToCompanyAPI = siretAPI ? siretAPI.startsWith(company.siren) : false;

        return newItem;
      });

      // sort to place first the copros that are not validated byAdmin (null)
      formattedData.sort((a, b) => {
        if (a.validatedByAdmin === null && b.validatedByAdmin === null) {return 0;}
        if (a.validatedByAdmin === null) {return -1;}
        if (b.validatedByAdmin === null) {return 1;}
        return 0;
      });

      setData(formattedData);
      
      const initialSelectedIds = new Set<number>();
      formattedData.forEach((item) => {
        if (item.VALIDE || item.isRepresentantLegalEqualToCompanyFile) {
          initialSelectedIds.add(item.id);
        }
      });
      setSelectedIds(initialSelectedIds);
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching data :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setStatus('ERROR');
    }
  };

  const handleCheckedDataChange = (checked: boolean, id: string) => {
    const numericId = Number(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(numericId);
      } else {
        next.delete(numericId);
      }
      return next;
    });
  };

  const handleSave = async(saveType: string) => {
    if (loading !== '') {return;}
    setLoading(saveType);

    if (!company || !jwt || !company.id || !data) {
      setLoading('');
      console.error('Informations obligatoires manquantes.');
      return;
    }

    if (selectedIds.size === 0) {
      setLoading('');
      console.error('Veuillez sélectionner au moins une copropriété.');
      return;
    }

    const processCoproData = async(copro) => {
      const isSelected = selectedIds.has(copro.id);
      switch (saveType) {
      case 'ACCEPT':
        if (isSelected && copro.validatedByAdmin !== true) {
          await setAdminValidation(copro.id, true);
        }
        break;
      case 'REFUSE':
        if (isSelected && copro.validatedByAdmin !== false) {
          await setAdminValidation(copro.id, false);
        }
        break;
      case 'SMART':
        if (isSelected) {
          await setAdminValidation(copro.id, true);
        } else {
          await setAdminValidation(copro.id, false);
        }
        break;
      }
    };

    try {
      await Promise.all(data.map((val) => processCoproData(val)));
      showToast('success', 'Les copropriétés ont bien été modifiées.');
      setLoading('');
      fetchData();
    } catch (errorSaving) {
      showToast('error', 'Une erreur est survenue lors de la modification des copropriétés.');
      setLoading('');
      console.error('Error saving data :', errorSaving);
    }
  };

  const openAddPopup = () => {
    setEditingCopro(null);
    setIsPopupOpen(true);
  };

  const selectAllRows = () => {
    if (data) {
      setSelectedIds(new Set(data.map((item) => item.id)));
    }
  };

  const deselectAllRows = () => {
    setSelectedIds(new Set());
  };

  const handleReverseBtn = () => {
    if (data) {
      const reversed = new Set<number>();
      data.forEach((item) => {
        if (!selectedIds.has(item.id)) {
          reversed.add(item.id);
        }
      });
      setSelectedIds(reversed);
    }
  };

  useEffect(() => {
    if (jwt && company && company.id && status !== 'PENDING') {
      fetchData();
    }
  }, [jwt, company]);

  return (
    <div>
      <div className="mb-8 overflow-x-scroll rounded-lg bg-white p-4 shadow-md">
        <p>
          À CE JOUR CE CABINET A VALIDÉ
          <span className="text-rgpd-blue-light">
            {' '}
            {data && data.length }
            {!data && <Skeleton height={15} width={30} inline />}
            {' '}
            IMMEUBLES
          </span>
          {' '}
          DONT
          <span className="text-rgpd-blue-light">
            {' '}
            {data && data.length > 0 && data.filter((item) => item['N° IMMAT']).length}
            {!data && <Skeleton height={15} width={30} inline />}
            {' '}
            COPROPRIÉTÉS HORS ASL, AFUL ET IMMEUBLES TERTIAIRES
          </span>
          {' '}
          SONT RATTACHÉES À SON NOM DANS LE REGISTRE DES COPROPRIÉTÉS
        </p>
        <div className="mt-6 flex w-full justify-center">
          <button onClick={openAddPopup} className="mx-auto mb-4 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto">
            <PlusIcon className="inline h-5 w-5 shrink-0" />
            {' '}
            Ajouter une copropriété
          </button>
        </div>
        {status === 'PENDING' && (
          <p className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </p>
        )}
        {status === 'SUCCESS' && (
          <>
            <h2 className="mt-8 mb-6 text-center text-xl text-rgpd-blue-light">LISTE DES IMMEUBLES AJOUTÉS</h2>
            <div className="mb-8 w-full md:flex">
              <button
                onClick={selectAllRows}
                className="mt-2 mr-2 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto"
              >
                Tout sélectionner
              </button>
              <button 
                onClick={deselectAllRows} 
                className="mt-2 mr-2 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto"
              >
                Tout désélectionner
              </button>
              <button 
                onClick={handleReverseBtn} 
                className="mt-2 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto"
              >
                Inverser la sélection
              </button>
            </div>
            <div className="mb-8 flex w-full items-center">
              <div className="mr-8 flex flex-col items-start">
                <div className="mb-2 flex items-center">
                  <div className="font-semibold">Légende pour les colonnes "REPRÉSENTANT LÉGAL" :</div>
                </div>
                <div className="flex items-center">
                  <div className="mr-2 flex flex-col justify-between">
                    <div className="mb-2 h-4 w-4 bg-rgpd-green" />
                    <div className="h-4 w-4 bg-red-600" />
                  </div>
                  <div>
                    <div>L'entreprise sélectionnée est le représentant légal dans le dernier import fichier/API RNIC</div>
                    <div>Une autre entreprise est le représentant légal dans le dernier import fichier/API RNIC</div>
                  </div>
                </div>
              </div>
            </div>
            <Table
              sortableColumns={sortableColumns}
              data={data.map((item) => ({
                ...item,
                VALIDE: selectedIds.has(item.id),
              }))}
              validColumnLabel="#"
              headers={['MODIFIER', 'N° IMMAT', 'NOM', 'ADRESSE', 'CP', 'COMMUNE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'REPRÉSENTANT LÉGAL', 'VALIDATION ADMIN', 'VALIDER']}
              tableClass="text-sm"
              columnChecking
              dataChangeCallback={handleCheckedDataChange}
              isSearchable
              idKey="id"
            />
            {data.length === 0 && (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucune copropriété trouvée"
                  className="my-8 mb-12"
                />
              </div>
            )}
            <div className="mt-6 w-full items-center md:flex">
              <button
                onClick={() => handleSave('REFUSE')}
                className="mt-2 mr-auto w-full rounded bg-rgpd-red px-4 py-6 text-white md:w-auto"
              >
                {loading === 'REFUSE' && <Spinner aria-label="Center-aligned spinner" size="lg" className="mx-8 my-3" />}
                Refuser les copropriétés sélectionnées
              </button>
              <button
                onClick={() => handleSave('SMART')}
                className="mx-auto mt-2 w-full rounded bg-rgpd-blue-light px-4 py-1 text-white md:w-auto"
              >
                {loading === 'SMART' && <Spinner aria-label="Center-aligned spinner" size="lg" className="mx-8 my-3" />}
                Accepter les copropriétés sélectionnées
                <br />
                &
                <br />
                Refuser les autres
              </button>
              <button
                onClick={() => handleSave('ACCEPT')}
                className="mt-2 ml-auto w-full rounded bg-rgpd-green px-4 py-6 text-white md:w-auto"
              >
                {loading === 'ACCEPT' && <Spinner aria-label="Center-aligned spinner" size="lg" className="mx-8 my-3" />}
                Valider les copropriétés sélectionnées
              </button>
            </div>
            <Popup
              isOpen={isPopupOpen}
              onClose={() => {
                setIsPopupOpen(false);
              }}
              title={editingCopro ? 'Modifier la copropriété' : 'Ajouter une nouvelle copropriété'}
              additionalClass="h-4/6 w-6/12"
              children={(
                <CoproForm
                  initialDataId={editingCopro}
                  setIsPopupOpen={setIsPopupOpen}
                  companyId={company.id}
                />
              )}
            />
          </>
        )}
        {status === 'ERROR' && (
          <div className="flex w-full place-content-center">
            <Badge
              mode="error"
              text="Erreur lors du chargement des données"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CoproValidation;
